/**
 * Searches to start from, by the visa type a firm's cases are on. Each query
 * is one the library answers with real authority for that case type. A visa
 * with no entry here gets no suggestion rather than a guess.
 */
import { VISAVALUE } from "@/types/visa.type";

export type VisaSuggestions = {
  visa: string;
  label: string;
  queries: string[];
};

const SUGGESTIONS: Record<string, { label: string; queries: string[] }> = {
  [VISAVALUE.EB1A]: {
    label: "EB-1A",
    queries: [
      "extraordinary ability sustained acclaim",
      "Matter of Chawathe final merits determination"
    ]
  },
  [VISAVALUE.EB2NIW]: {
    label: "EB-2 NIW",
    queries: [
      "Matter of Dhanasar national interest waiver",
      "national importance prong NIW"
    ]
  },
  [VISAVALUE.EB2NIW_V2]: {
    label: "EB-2 NIW",
    queries: [
      "Matter of Dhanasar national interest waiver",
      "national importance prong NIW"
    ]
  },
  [VISAVALUE.O1A]: {
    label: "O-1A",
    queries: ["O-1A comparable evidence", "Judging the work of others O-1A"]
  },
  [VISAVALUE.O1B]: {
    label: "O-1B",
    queries: ["O-1B extraordinary achievement motion picture television"]
  },
  [VISAVALUE.H1B]: {
    label: "H-1B",
    queries: ["specialty occupation H-1B", "H-1B fee"]
  },
  [VISAVALUE.L1A]: {
    label: "L-1A",
    queries: ["L-1A qualifying relationship new office", "Matter of Chawathe"]
  },
  [VISAVALUE.PERM]: {
    label: "PERM",
    queries: ["PERM prevailing wage labor certification", "8 CFR 204.5(g)"]
  },
  [VISAVALUE.AOS]: {
    label: "Adjustment of Status",
    queries: ["INA 245 adjustment of status", "8 CFR 245.1"]
  },
  [VISAVALUE.I589]: {
    label: "Asylum",
    queries: [
      "particular social group firm resettlement",
      "INA 208 asylum one year bar"
    ]
  },
  [VISAVALUE.I130]: {
    label: "I-130",
    queries: ["I-130 bona fide marriage", "INA 201 immediate relative"]
  },
  [VISAVALUE.I751]: {
    label: "I-751",
    queries: [
      "I-751 joint filing good faith marriage",
      "INA 216 conditional residence"
    ]
  },
  [VISAVALUE.I601]: {
    label: "I-601",
    queries: [
      "I-601 extreme hardship waiver",
      "INA 212(a)(9)(B) unlawful presence"
    ]
  },
  [VISAVALUE.N400]: {
    label: "N-400",
    queries: ["N-400 good moral character", "8 CFR 316.10"]
  },
  [VISAVALUE.TN]: {
    label: "TN",
    queries: ["TN professional NAFTA USMCA", "8 CFR 214.6"]
  }
};

/**
 * The visa types to suggest for, most cases first: the ones with a
 * suggestion, up to `limit`. Visa types the firm has no suggestions for are
 * skipped.
 */
export const suggestionsFor = (
  counts: ReadonlyArray<{ visa: string; count: number }>,
  limit = 3
): VisaSuggestions[] => {
  const out: VisaSuggestions[] = [];
  const seen = new Set<string>();
  const ordered = [...counts].sort((a, b) => b.count - a.count);
  for (const { visa } of ordered) {
    const entry = SUGGESTIONS[visa];
    if (entry && !seen.has(entry.label)) {
      seen.add(entry.label);
      out.push({ visa, label: entry.label, queries: entry.queries });
    }
    if (out.length === limit) break;
  }
  return out;
};
